/**
 * Where the bubble goes (build decisions §3): next to the device, flipped to
 * stay inside the map frame, never over the device it is about.
 */
export const BUBBLE_WIDTH_PX = 312;
/** From the device to the bubble's edge: room for the tail. */
export const BUBBLE_GAP_PX = 14;
export const BUBBLE_MARGIN_PX = 8;
/** The tail stays this far from a corner of the bubble. */
const TAIL_INSET_PX = 16;

export type BubbleTail = 'bottom' | 'top' | 'left' | 'right';

export interface BubblePlaceInput {
  /** The device, in the frame's coordinates. */
  readonly ax: number;
  readonly ay: number;
  /** The bubble's size. */
  readonly bw: number;
  readonly bh: number;
  /** The frame's size. */
  readonly fw: number;
  readonly fh: number;
}

export interface BubblePlacement {
  readonly left: number;
  readonly top: number;
  /** Which edge of the bubble carries the tail. */
  readonly tail: BubbleTail;
  /** Along that edge, from its start. */
  readonly tailAt: number;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi));

export function placeBubble({ ax, ay, bw, bh, fw, fh }: BubblePlaceInput): BubblePlacement {
  const m = BUBBLE_MARGIN_PX;
  const g = BUBBLE_GAP_PX;
  const fitsAbove = ay - g - bh >= m;
  const fitsBelow = ay + g + bh <= fh - m;
  if (fitsAbove || fitsBelow) {
    const left = clamp(ax - bw / 2, m, fw - bw - m);
    return {
      left,
      top: fitsAbove ? ay - g - bh : ay + g,
      tail: fitsAbove ? 'bottom' : 'top',
      tailAt: clamp(ax - left, TAIL_INSET_PX, bw - TAIL_INSET_PX),
    };
  }
  // Too tall for either side of it: beside it, where there is room.
  const top = clamp(ay - bh / 2, m, fh - bh - m);
  const fitsRight = ax + g + bw <= fw - m;
  const fitsLeft = ax - g - bw >= m;
  if (fitsRight || fitsLeft) {
    return {
      left: fitsRight ? ax + g : ax - g - bw,
      top,
      tail: fitsRight ? 'left' : 'right',
      tailAt: clamp(ay - top, TAIL_INSET_PX, bh - TAIL_INSET_PX),
    };
  }
  // A frame too small for any of it: the side with more room, kept inside.
  const below = fh - ay > ay;
  const left = clamp(ax - bw / 2, m, fw - bw - m);
  return {
    left,
    top: clamp(below ? ay + g : ay - g - bh, m, fh - bh - m),
    tail: below ? 'top' : 'bottom',
    tailAt: clamp(ax - left, TAIL_INSET_PX, bw - TAIL_INSET_PX),
  };
}

/** Whether the point is inside the frame: a device panned out of view has
 *  nothing to point a tail at. */
export function inFrame(ax: number, ay: number, fw: number, fh: number): boolean {
  return ax >= 0 && ay >= 0 && ax <= fw && ay <= fh;
}
